



























































import { PropType } from 'vue';
import { ErrorManager } from '@/models/error';

import mixins from '@/utils/mixins';
import Functions from '@/mixins/functions';
import { IQuote, IQuoteData, Quote, QuoteType } from '@/models';
import { mapActions, mapGetters } from 'vuex';
import { IQuoteFilterParam } from '@/services';
import { getPropertyFromItem } from '@/utils/helpers';

import QuoteExpiredLabel from './QuoteExpiredLabel.vue';

export default mixins(Functions).extend({
  name: 'QuotePicker',

  model: {
    prop: 'value',
    event: 'input'
  },

  components: {
    QuoteExpiredLabel
  },

  props: {
    value: [Number, Object, Array] as PropType<
      (IQuote | number)[] | IQuote | number
    >,

    externalErrorMessage: {
      type: Array as PropType<string[]>,
      default: () => []
    },

    filter: {
      type: Object as PropType<IQuoteFilterParam>
    }
  },

  data() {
    return {
      lazyValue: this.value,
      localQuoteList: [] as IQuote[],
      search: '',
      errorMessage: '',
      detailErrorMessage: '',
      loading: false
    };
  },

  computed: {
    ...mapGetters({
      getQuotes: 'quote/getQuotes'
    }),

    internalValue: {
      get(): any {
        return this.lazyValue;
      },
      set(val: any) {
        this.lazyValue = val;
        this.$emit('input', val);
      }
    },

    customAttrs(): object {
      return this.$attrs;
    },

    errorMessageList(): string[] {
      let errorList: string[] = [];
      if (this.externalErrorMessage) {
        errorList = this.externalErrorMessage;
      }
      if (this.errorMessage) {
        errorList.push(this.errorMessage);
      }

      return errorList;
    }
  },

  watch: {
    search: {
      handler(val) {
        this.debounceFetchQuotes(typeof val == 'object' ? val.toString() : val);
      },
      immediate: true
    },

    value: {
      handler(val) {
        this.syncSelectedQuotes(val);
      },
      immediate: true
    }
  },

  mounted() {
    if (this.getQuotes && this.getQuotes.length > 0) {
      this.getQuotes.forEach((storeQuote: IQuote) => {
        const exists = this.localQuoteList.some(q => q.id === storeQuote.id);
        if (!exists) {
          this.localQuoteList.push(this.hydrateQuote(storeQuote));
        }
      });
    }
  },

  methods: {
    ...mapActions({
      fetchQuotes: 'quote/fetchQuotes'
    }),

    syncSelectedQuotes(val: (IQuote | number)[] | IQuote | number | null) {
      if (!val) {
        return;
      }

      const valuesToAdd = Array.isArray(val) ? val : [val];
      valuesToAdd.forEach(item => {
        if (!item || typeof item !== 'object') {
          return;
        }

        const hydratedItem = this.hydrateQuote(item);
        const exists = this.localQuoteList.some(q => q.id === hydratedItem.id);
        if (!exists) {
          this.localQuoteList.push(hydratedItem);
        }
      });
    },

    clearAllErrors() {
      this.errorMessage = '';
      this.detailErrorMessage = '';
    },

    debounceFetchQuotes(search?: string) {
      this.clearAllErrors();

      if (typeof search == 'object') return;

      this.loading = true;
      let params: IQuoteFilterParam = {
        page: 1,
        search: search
      };

      if (this.filter) {
        params = {
          ...params,
          ...this.filter
        };
      }

      this.debounce(this.searchQuotes, 1000)(params);
    },

    searchQuotes(params: IQuoteFilterParam) {
      this.loading = true;
      return this.$services.quote
        .fetchQuotes(params)
        .then(data => {
          const selectedIds = new Set<number>();
          if (this.value) {
            const values = Array.isArray(this.value)
              ? this.value
              : [this.value];
            values.forEach(v => {
              if (typeof v === 'object' && v?.id) {
                selectedIds.add(v.id);
              } else if (typeof v === 'number') {
                selectedIds.add(v);
              }
            });
          }

          const mergedById = new Map<number, IQuote>();

          data.results.forEach((quoteData: IQuoteData) => {
            const quote = new Quote(quoteData);
            mergedById.set(quote.id!, quote);
          });

          this.localQuoteList.forEach(existingQuote => {
            const hydrated = this.hydrateQuote(existingQuote);
            const quoteId = hydrated.id!;
            if (selectedIds.has(quoteId) && !mergedById.has(quoteId)) {
              mergedById.set(quoteId, hydrated);
            }
          });

          this.localQuoteList = Array.from(mergedById.values());
        })
        .catch(error => {
          this.errorMessage = ErrorManager.extractApiError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    /**
     * removing filter from the quote search as to get the raw data from the server as is
     */
    itemSearch(item: any, queryText: string, itemText: string) {
      return true;
    },

    getQuoteValue(quote: IQuoteData) {
      return getPropertyFromItem(quote, 'number', quote);
    },

    stringRepresentation(quote: IQuote) {
      return this.hydrateQuote(quote).toCustomerProposalString();
    },

    extractCustomerName(quote: IQuote): string {
      return this.hydrateQuote(quote).customer?.name ?? '';
    },

    formatQuoteLabel(quote: IQuote): string {
      return this.hydrateQuote(quote).toString();
    },

    hydrateQuote(quote: IQuote | Quote): Quote {
      return quote instanceof Quote ? quote : new Quote(quote);
    }
  }
});
